import React, { useRef,  useState, useEffect } from 'react';
import Topbar from '../components/Topbar';
import { getAirlines, createAirline, updateAirline, deleteAirline, updateAirlineStatus } from '../services/airlineService';
import { getAirports } from '../services/airportService';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import { useRefresh } from '../utils/RefreshContext';

const Svg = ({ d, ...props }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8" strokeLinecap="round" {...props}>
    <path d={d} />
  </svg>
);

const Airlines = () => {
  const { refreshKey } = useRefresh();
  const [airlines, setAirlines] = useState([]);
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentIata, setCurrentIata] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showAirportDropdown, setShowAirportDropdown] = useState(false);

  // Custom Modal State
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Pagination & Filter States
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    iata: '',
    icao: '',
    airports: [],
    status: 1,
    logo: null,
    logoPreview: ''
  });

  useEffect(() => {
    const fetchAirports = async () => {
      try {
        const res = await getAirports();
        setAirports(res?.data || []);
      } catch (err) { console.error(err); }
    };
    fetchAirports();
  }, []);
  const prevSearch = useRef(search);

  useEffect(() => {
    const searchChanged = prevSearch.current !== search;
    prevSearch.current = search;

    const timer = setTimeout(() => {
      if (searchChanged && page !== 1) {
        setPage(1);
      } else {
        fetchData();
      }
    }, searchChanged ? 500 : 0);

    return () => clearTimeout(timer);
  }, [page, limit, filterStatus, refreshKey, search]);


  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        search: search || undefined,
        status: filterStatus === 'All' ? undefined : (filterStatus === 'Active' ? 1 : 0)
      };
      const airlineRes = await getAirlines(params);
      setAirlines(airlineRes?.data || []);
      setTotalPages(airlineRes?.meta?.totalPages || 1);
      setTotalRecords(airlineRes?.meta?.total || 0);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error("Failed to load airlines");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (a) => {
    setIsEditing(true);
    setCurrentIata(a.iata);
    setFormData({
      name: a.name || '',
      iata: a.iata || '',
      icao: a.icao || '',
      airports: Array.isArray(a.airports) ? a.airports.map(ap => ap.id || ap) : [],
      status: Number(a.status) || 1,
      logo: null,
      logoPreview: a.logo ? `${process.env.REACT_APP_IMG_BASE_URL}${a.logo}` : ''
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const confirmDelete = (a) => {
    setDeleteTarget(a);
    setShowConfirm(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAirline(deleteTarget.iata);
      toast.success("Airline deleted successfully");
      setShowConfirm(false);
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        logo: file,
        logoPreview: URL.createObjectURL(file)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.iata) {
      return toast.warning("Please fill all required fields");
    }

    if (/\s/.test(formData.iata)) {
      return toast.warning("IATA Code cannot include white spaces");
    }

    if (formData.icao && formData.icao.length > 4) {
      return toast.warning("ICAO Code cannot exceed 4 characters");
    }

    if (formData.icao && /\s/.test(formData.icao)) {
      return toast.warning("ICAO Code cannot include white spaces");
    }

    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append('name', formData.name);
      payload.append('iata', formData.iata);
      if (formData.icao) {
        payload.append('icao', formData.icao);
      }
      payload.append('status', formData.status);
      
      if (formData.airports && formData.airports.length > 0) {
        formData.airports.forEach(ap => {
          payload.append('airports[]', ap);
        });
      }
      
      if (formData.logo instanceof File) {
        payload.append('logo', formData.logo);
      }

      const response = isEditing 
        ? await updateAirline(currentIata, payload)
        : await createAirline(payload);

      if (response && (response.status === 200 || response.status === 201)) {
        toast.success(`Airline ${isEditing ? 'updated' : 'created'} successfully`);
        resetForm();
        fetchData();
      } else {
        toast.error(response?.data?.message || "Operation failed");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', iata: '', icao: '', airports: [], status: 1, logo: null, logoPreview: '' });
    setIsEditing(false);
    setCurrentIata(null);
    setShowForm(false);
    setShowAirportDropdown(false);
  };

  const handleToggleStatus = async (a) => {
    try {
      const newStatus = Number(a.status) === 1 ? 0 : 1;
      await updateAirlineStatus(a.iata, { status: newStatus });
      toast.success(`Airline ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const toggleAirport = (id) => {
    setFormData(prev => {
      const exists = prev.airports.includes(id);
      return {
        ...prev,
        airports: exists ? prev.airports.filter(a => a !== id) : [...prev.airports, id]
      };
    });
  };

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(page - 1);
        pages.push(page);
        pages.push(page + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <Topbar title="Airline Management" subtitle="Manage airline operators and their airport presence" />
      
      {/* CUSTOM CONFIRMATION MODAL */}
      {showConfirm && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, backdropFilter: 'blur(4px)' }}>
          <div className="modal-card" style={{ background: 'white', padding: 32, borderRadius: 20, width: '100%', maxWidth: 400, textAlign: 'center', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ width: 64, height: 64, background: '#FFF5F5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2"><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--dark)', marginBottom: 10 }}>Confirm Deletion</h3>
            <p style={{ fontSize: 14, color: 'var(--grey)', marginBottom: 24, lineHeight: 1.5 }}>
              Are you sure you want to delete <span style={{ fontWeight: 700, color: 'var(--navy)' }}>{deleteTarget?.name} ({deleteTarget?.type})</span>? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1, background: '#F87171', borderColor: '#F87171' }} onClick={handleDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="content">
        <div className="screen-label">5.1 — Airline Management</div>

        {/* CONDITIONAL FORM DISPLAY */}
        {showForm && (
          <div className="card" style={{ marginBottom: 24, border: '2px solid var(--blue)' }}>
            <div style={{ padding: '20px 26px' }}>
              <div className="screen-label" style={{ marginBottom: 14, fontSize: 12, color: 'var(--blue)' }}>{isEditing ? 'Editing Airline' : 'Add New Airline'}</div>
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', gap: 24, marginBottom: 24 }}>
                  {/* Left Column - Form Fields */}
                  <div style={{ flex: 1 }}>
                    <div className="form-grid">
                      <div className="form-group full">
                        <label className="form-label">Airline Name <span className="req">*</span></label>
                        <input 
                          className="form-input" 
                          placeholder="e.g. American Airlines" 
                          value={formData.name}
                          onChange={e => setFormData({...formData, name: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">IATA Code <span className="req">*</span></label>
                        <input 
                          className="form-input" 
                          placeholder="e.g. AA" 
                          style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }} 
                          disabled={isEditing}
                          value={formData.iata}
                          onChange={e => setFormData({...formData, iata: e.target.value.toUpperCase()})}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">ICAO Code</label>
                        <input 
                          className="form-input" 
                          placeholder="e.g. AAL" 
                          style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }} 
                          maxLength={4}
                          value={formData.icao}
                          onChange={e => setFormData({...formData, icao: e.target.value.toUpperCase()})}
                        />
                      </div>

                      {/* PILL-BASED MULTI-SELECT AIRPORT INPUT */}
                      <div className="form-group full">
                        <label className="form-label">Associated Airports</label>
                        <div className="multi-select-container" style={{ position: 'relative' }}>
                          <div className="multi-select-input" style={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 8, 
                            padding: '10px 14px', 
                            border: '1px solid #E2E8F0', 
                            borderRadius: 12,
                            minHeight: 48,
                            alignItems: 'center',
                            background: 'white'
                          }}>
                            {formData.airports.map(id => {
                              const ap = airports.find(a => a.id === id);
                              return (
                                <div key={id} className="ms-pill" style={{ 
                                  background: '#EEF6FF', 
                                  color: 'var(--blue)', 
                                  padding: '4px 10px', 
                                  borderRadius: 8, 
                                  fontSize: 13, 
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6
                                }}>
                                  {ap?.code || id}
                                  <span onClick={(e) => { e.stopPropagation(); toggleAirport(id); }} style={{ cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</span>
                                </div>
                              );
                            })}
                            <div 
                              className="ms-add" 
                              onClick={() => setShowAirportDropdown(!showAirportDropdown)}
                              style={{ color: 'var(--grey)', fontSize: 13, cursor: 'pointer', userSelect: 'none', paddingLeft: formData.airports.length > 0 ? 8 : 0 }}
                            >
                              + Add airport
                            </div>
                          </div>

                          {showAirportDropdown && (
                            <div className="ms-dropdown" style={{ 
                              position: 'absolute', 
                              top: '100%', 
                              left: 0, 
                              right: 0, 
                              zIndex: 100, 
                              background: 'white', 
                              border: '1px solid #E2E8F0', 
                              borderRadius: 12, 
                              marginTop: 6, 
                              boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                              maxHeight: 200,
                              overflowY: 'auto',
                              padding: 6
                            }}>
                              {airports.filter(ap => !formData.airports.includes(ap.id)).map(ap => (
                                <div 
                                  key={ap.id} 
                                  onClick={() => { toggleAirport(ap.id); setShowAirportDropdown(false); }}
                                  style={{ padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, transition: '0.2s' }}
                                  onMouseEnter={e => e.target.style.background = '#F8FAFC'}
                                  onMouseLeave={e => e.target.style.background = 'transparent'}
                                >
                                  <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{ap.code}</span> — {ap.name}
                                </div>
                              ))}
                              {airports.filter(ap => !formData.airports.includes(ap.id)).length === 0 && (
                                <div style={{ padding: '12px', textAlign: 'center', color: 'var(--grey)', fontSize: 12 }}>No more airports to add</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="form-group full"><label className="form-label">Status <span className="req">*</span></label>
                        <div className="toggle-row">
                          <div><div style={{ fontSize: 13, fontWeight: 600 }}>Active in app</div><div style={{ fontSize: 11, color: 'var(--grey)' }}>Active = shown in available airlines</div></div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 12, color: 'var(--grey)' }}>Inactive</span>
                            <button 
                              type="button"
                              className={`toggle-switch ${Number(formData.status) === 1 ? 'on' : ''}`} 
                              onClick={() => setFormData({...formData, status: Number(formData.status) === 1 ? 0 : 1})}
                            />
                            <span style={{ fontSize: 12, fontWeight: 700, color: Number(formData.status) === 1 ? 'var(--green)' : 'var(--grey)' }}>Active</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Logo Upload */}
                  <div style={{ width: 280 }}>
                    <label className="form-label" style={{ marginBottom: 12, display: 'block' }}>Airline Logo / Photo <span style={{ fontSize: 11, color: 'var(--grey)', fontWeight: 400 }}>OPTIONAL</span></label>
                    <div style={{
                      border: '2px solid #E2E8F0',
                      borderRadius: 12,
                      padding: 20,
                      minHeight: 240,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative',
                      background: formData.logoPreview ? 'transparent' : '#F8FAFC',
                      borderColor: formData.logoPreview ? '#0078D2' : '#E2E8F0'
                    }}
                    onClick={() => document.getElementById('logo-input')?.click()}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = '#0078D2';
                      e.currentTarget.style.background = '#F0F8FF';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = formData.logoPreview ? '#0078D2' : '#E2E8F0';
                      e.currentTarget.style.background = formData.logoPreview ? 'transparent' : '#F8FAFC';
                    }}>
                      {formData.logoPreview ? (
                        <>
                          <img 
                            src={formData.logoPreview} 
                            alt="Logo Preview" 
                            style={{ maxWidth: '100%', maxHeight: 180, objectFit: 'contain', marginBottom: 12, borderRadius: 8 }}
                          />
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', marginBottom: 4 }}>Change Logo</div>
                            <div style={{ fontSize: 11, color: 'var(--grey)' }}>JPG, PNG • Max 5MB</div>
                          </div>
                        </>
                      ) : (
                        <>
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5" style={{ marginBottom: 12 }}>
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="17 8 12 3 7 8"/>
                            <line x1="12" y1="3" x2="12" y2="15"/>
                          </svg>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', marginBottom: 4 }}>Upload Airline Logo</div>
                            <div style={{ fontSize: 11, color: 'var(--grey)' }}>JPG, PNG • Max 5MB</div>
                          </div>
                        </>
                      )}
                      <input 
                        id="logo-input"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-footer" style={{ paddingLeft: 0, paddingRight: 0, marginTop: 20 }}>
                  <button type="button" className="btn btn-outline" onClick={resetForm}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/></svg>
                    {submitting ? 'Saving...' : isEditing ? 'Update Airline' : 'Save Airline'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="card">
          <div className="toolbar">
            <div className="search-wrap">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <input 
                className="search-input" 
                placeholder="Search by name or IATA code..." 
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select 
              className="filter-select" 
              value={filterStatus}
              onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
            >
              <option value="All">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
            {!showForm && (
              <button className="btn btn-primary" onClick={() => setShowForm(true)}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>Add Airline</button>
            )}
          </div>
          <table className="tbl">
            <thead><tr><th>Logo</th><th>Name</th><th>IATA</th><th>ICAO</th><th>Associated Airports</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {airlines.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: 'var(--grey)' }}>No airlines found</td></tr>
              ) : (
                airlines.map((a, i) => (
                  <tr key={a.iata || i}>
                    <td>
                      {a.logo ? (
                        <img 
                          src={`${process.env.REACT_APP_IMG_BASE_URL}${a.logo}`} 
                          alt={a.iata} 
                          style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }}
                        />
                      ) : (
                        <div className="airline-logo" style={{ background: '#0078D2' }}>{a.iata || 'N/A'}</div>
                      )}
                    </td>
                    <td style={{ fontWeight: 600, color: 'var(--navy)' }}>{a.name}</td>
                    <td><span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, letterSpacing: 1 }}>{a.iata}</span></td>
                    <td><span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, letterSpacing: 1 }}>{a.icao}</span></td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {Array.isArray(a.airports) && a.airports.map((ap, idx) => (
                          <span key={idx} className="tag">{typeof ap === 'object' ? ap.code : ap}</span>
                        ))}
                        {(!a.airports || a.airports.length === 0) && (
                          <span style={{ fontSize: 12, color: 'var(--grey)' }}>None</span>
                        )}
                      </div>
                    </td>
                    <td><span className={`badge badge-${Number(a.status) === 1 ? 'active' : 'inactive'}`}>{Number(a.status) === 1 ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <div className="action-btns">
                        <button className="action-btn" title={Number(a.status) === 1 ? "Deactivate" : "Activate"} onClick={() => handleToggleStatus(a)}>
                          {Number(a.status) === 1 ? (
                            <Svg d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          ) : (
                            <Svg d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                          )}
                        </button>
                        <button className="action-btn" onClick={() => handleEdit(a)}><Svg d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></button>
                        <button className="action-btn danger" onClick={() => confirmDelete(a)}><Svg d="M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* PAGINATION CONTROLS */}
          {airlines.length > 0 && (
            <div className="pagination-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 26px', borderTop: '1px solid #F0F4F8' }}>
              <div style={{ fontSize: 13, color: 'var(--grey)' }}>
                Showing <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{((page - 1) * limit) + 1}</span> to <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{Math.min(page * limit, totalRecords)}</span> of <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{totalRecords}</span> entries
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button 
                  className="btn btn-outline" 
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  style={{ padding: '6px 12px', fontSize: 12 }}
                >
                  Previous
                </button>
                <div style={{ display: 'flex', gap: 4 }}>
                  {renderPagination().map((p, i) => (
                    <button 
                      key={i}
                      className={`btn ${page === p ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => typeof p === 'number' && setPage(p)}
                      style={{ 
                        padding: '6px 12px', 
                        fontSize: 12, 
                        minWidth: 36,
                        cursor: typeof p === 'number' ? 'pointer' : 'default',
                        border: typeof p === 'number' ? '' : 'none',
                        background: typeof p === 'number' ? '' : 'transparent'
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <button 
                  className="btn btn-outline" 
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                  style={{ padding: '6px 12px', fontSize: 12 }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Airlines;
